import { SIZE } from 'baseui/input';
import React, { FC } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { setInvoiceAddress } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import store from 'store';
import { FormValidator } from 'tools/FormValidator';
import { Button } from '@techem/ui-components';
import FormControl from 'presentation/components/FormControl';
import { Controller, useForm } from 'react-hook-form';
import Input from 'presentation/components/Input';
import { KIND } from 'baseui/button';

interface EditInvoiceAddressViewProperties {
  callbackOnSuccess: () => void;
  callbackOnAbort: () => void;
}
interface FormData {
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
}
const EditInvoiceAddressView: FC<EditInvoiceAddressViewProperties> = (props) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: store.getState().onboardingInfo.invoiceAddress
  });
  const onSubmit = handleSubmit(({ street, houseNumber, zip, city }, e) => {
    e?.preventDefault();
    store.dispatch(
      setInvoiceAddress({
        street,
        houseNumber,
        zip,
        city
      })
    );

    props.callbackOnSuccess();
  });
  return (
    <Col xs={12} className="my-4">
      <form noValidate onSubmit={onSubmit} aria-labelledby="edit-invoice-address-title">
        <h2 id="edit-invoice-address-title" className="visually-hidden">
          {t('summaryEditInvoiceAddressTitle')}
        </h2>
        <Row>
          <Col xs={12} style={{ marginBottom: '-8px' }}>
            <FormControl
              required
              label={() => (
                <label htmlFor="invoice-street-input">{t('pageInvoiceAddressLabelStreet')}</label>
              )}
              error={errors.street && t('pageInvoiceAddressErrorStreet')}
            >
              <>
                <Controller
                  name="street"
                  control={control}
                  rules={{
                    required: true,
                    validate: {
                      validate: (v) => FormValidator.isValidStreet(v)
                    }
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      id="invoice-street-input"
                      value={value}
                      onChange={onChange}
                      error={!!errors.street}
                      size={SIZE.default}
                      additionalTestId="street"
                      aria-invalid={!!errors.street}
                      aria-describedby={errors.street ? 'invoice-street-error' : undefined}
                      autoComplete="street-address"
                    />
                  )}
                />
                {errors.street && (
                  <span id="invoice-street-error" className="visually-hidden" aria-live="polite">
                    {t('pageInvoiceAddressErrorStreet')}
                  </span>
                )}
              </>
            </FormControl>
          </Col>
          <Col xs={12}>
            <FormControl
              required
              label={() => (
                <label htmlFor="invoice-houseNumber-input">
                  {t('pageInvoiceAddressLabelHouseNumber')}
                </label>
              )}
            >
              <Controller
                name="houseNumber"
                control={control}
                rules={{
                  required: true,
                  validate: {
                    validate: (v) => FormValidator.isValidHouseNumber(v)
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    id="invoice-houseNumber-input"
                    value={value}
                    onChange={onChange}
                    error={!!errors.houseNumber}
                    size={SIZE.default}
                    additionalTestId="houseNumber"
                    aria-invalid={!!errors.houseNumber}
                    aria-describedby={errors.houseNumber ? 'invoice-housenumber-error' : undefined}
                    autoComplete="street-house-number"
                  />
                )}
              />
            </FormControl>
          </Col>
          <Col xs={12}>
            <FormControl
              required
              label={() => (
                <label htmlFor="invoice-zip-input">{t('pageInvoiceAddressLabelZIPCode')}</label>
              )}
              error={errors.zip && t('pageInvoiceAddressErrorZIPCode')}
            >
              <Controller
                name="zip"
                control={control}
                rules={{
                  required: true,
                  validate: {
                    validate: (v) => FormValidator.isValidZIPCode(v)
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    id="invoice-zip-input"
                    type="text"
                    value={value}
                    onChange={onChange}
                    error={!!errors.zip}
                    size={SIZE.default}
                    additionalTestId="zipCode"
                    aria-invalid={!!errors.zip}
                    aria-describedby={errors.zip ? 'invoice-zip-error' : undefined}
                    autoComplete="postal-code"
                  />
                )}
              />
            </FormControl>
          </Col>
          <Col xs={12}>
            <FormControl
              required
              label={() => (
                <label htmlFor="invoice-city-input">{t('pageInvoiceAddressLabelCity')}</label>
              )}
              error={errors.city && t('pageInvoiceAddressErrorCity')}
            >
              <Controller
                name="city"
                control={control}
                rules={{
                  required: true,
                  validate: {
                    validate: (v) => FormValidator.isValidCity(v)
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    id="invoice-city-input"
                    type="text"
                    value={value}
                    onChange={onChange}
                    error={!!errors.city}
                    size={SIZE.default}
                    additionalTestId="city"
                    aria-invalid={!!errors.city}
                    aria-describedby={errors.city ? 'invoice-city-error' : undefined}
                    autoComplete="address-level2"
                  />
                )}
              />
            </FormControl>
          </Col>

          <Col xs={12} md="auto">
            <Row className="justify-content-center">
              <Col xs="auto" className="my-1 order-sm-last order-md-first text-center">
                <Button size={SIZE.large} type="submit">
                  {t('summaryEditSaveButtonText')}
                </Button>
              </Col>
              <Col xs={12} sm="auto" className="my-1 text-center">
                <Button
                  kind={KIND.secondary}
                  size={SIZE.large}
                  onClick={() => {
                    props.callbackOnAbort();
                  }}
                >
                  {t('summaryEditAbortButtonText')}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </Col>
  );
};

export default EditInvoiceAddressView;
